import axiosInstances from 'src/utils/axiosInstances';
import extractCall, { extractScreenpopCore } from 'src/utils/five9/extractCall';

const dispatchQoreAxios = axiosInstances({
  baseURL: process.env.REACT_APP_DISPATCH_QORE_API,
  headers: {
    'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY,
  },
  withCredentials: true,
});

const EVENT_ALLOWLIST = new Set([
  'interaction.callStarted',
  'interaction.callAccepted',
  'interaction.callEnded',
  'interaction.callFinished',
  'crm.search',
  'crm.saveLog',
  'crm.screenPop',
  'crm.beforeCallFinished'
]);

const SESSION_KEY = 'dispatchQore.five9.clientSessionId';

const createLocalId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getClientSessionId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.sessionStorage?.getItem(SESSION_KEY);
    if (existing) return existing;
    const generated = createLocalId();
    window.sessionStorage?.setItem(SESSION_KEY, generated);
    return generated;
  } catch (_) {
    return null;
  }
};

const getPageContext = () => {
  if (typeof window === 'undefined') {
    return {
      screenUrl: null,
      screenPath: null,
      screenSearch: null,
      screenHash: null,
    };
  }

  const { location } = window;
  const pathname = location?.pathname || null;
  const screenName = pathname
    ? pathname.split('/').filter(Boolean).slice(-1)[0] || null
    : null;
  return {
    screenUrl: location?.href || null,
    screenPath: pathname,
    screenName,
    screenSearch: location?.search || null,
    screenHash: location?.hash || null,
  };
};

export const shouldPublishFive9Event = (type) => EVENT_ALLOWLIST.has(type);

const normalizeRawPayload = (entry) => {
  const raw = entry?.payload;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw || null;
  const keys = Object.keys(raw);
  if (keys.length === 1 && keys[0] === 'payload' && raw.payload && typeof raw.payload === 'object') {
    return raw.payload;
  }
  return raw;
};

export const buildFive9EventEnvelope = (entry, context = {}) => {
  const rawPayload = normalizeRawPayload(entry);
  const call = extractCall(rawPayload);
  const payloadScreenpopCore = call?.screenpopCore || extractScreenpopCore([
    rawPayload?.callData,
    rawPayload?.interactionData,
    rawPayload?.interactionSearchData,
    rawPayload?.interactionLogData,
    rawPayload?.callLogData,
    rawPayload
  ]);
  const payloadCallData = rawPayload?.callData || rawPayload?.interactionData || {};
  const eventId = typeof entry?.id === 'string' && entry.id ? entry.id : createLocalId();
  const pageContext = getPageContext();

  return {
    eventId,
    eventType: entry?.type || 'unknown',
    timestamp: entry?.ts || Date.now(),
    correlationId: eventId,
    clientSessionId: getClientSessionId(),
    level: entry?.level || 'info',
    message: entry?.message || '',
    callId: call?.callId || payloadCallData?.callId || null,
    callSid: call?.callSid || payloadCallData?.interactionId || payloadCallData?.callSid || null,
    // Unambiguous name for what callSid has historically carried (per-leg id).
    interactionId: call?.five9InteractionId || payloadCallData?.interactionId || call?.callSid || null,
    sessionId: call?.sessionId || payloadCallData?.sessionId || null,
    source: 'nsd-fe-dispatch-qore',
    context: {
      activeDispatchId: context?.activeDispatchId || null,
      currentUserId: context?.user?.id || null,
      currentUserEmail: context?.user?.email || null,
      currentUserName: context?.user?.fullName || null,
      screenpopCore: context?.screenpopCore
        || (Object.keys(payloadScreenpopCore).length ? payloadScreenpopCore : null),
      screenUrl: pageContext.screenUrl,
      screenPath: pageContext.screenPath,
      screenName: pageContext.screenName,
      screenSearch: pageContext.screenSearch,
      screenHash: pageContext.screenHash,
    },
    payload: rawPayload,
  };
};

export const publishFive9Event = async (entry, context = {}) => {
  if (!shouldPublishFive9Event(entry?.type)) return;

  const envelope = buildFive9EventEnvelope(entry, context);
  try {
    await dispatchQoreAxios.post('/five9-events-ingest', envelope, {
      headers: {
        'X-Correlation-ID': envelope.correlationId,
      },
    });
  } catch (error) {
    // Event publishing is best-effort and must never disrupt call handling.
    // eslint-disable-next-line no-console
    console.warn('[five9EventsService] publish failed', {
      eventType: envelope.eventType,
      eventId: envelope.eventId,
      status: error?.response?.status,
      message: error?.message,
    });
  }
};
